import * as ng from 'angular';

import {FilterServiceFactory, FilterService, DataList} from '../../shared';
import {EventsApi, Event, EventDetail} from '.';

// ngInclude's in event-list.html
require('./details-view.html');
require('./note/view.html');

export class EventListController {
   static $inject = [
      'eventsApi', 'session', 'dialogs',  'toast',
      'filterServiceFactory', 'loader', 'titleService'
   ];

   filter:FilterService;
   model = new DataList<Event, EventDetail>();
   activeTab:number;

   constructor(
      private eventsApi:EventsApi,
      private session,
      private dialogs,
      private toast,
      filterServiceFactory:FilterServiceFactory,
      private loader,
      titleService
   )
   {
      titleService.setTitle('Events');

      this.filter = filterServiceFactory({
         key: 'events',
         defaults: {
            campaignId: this.session.getCampaign().id,
            orderBy: '-startDate',
            limit: 20,
            page: 1
         },
         properties: ['search', 'startDate','endDate'],
         callback: (property:string) => this.refresh(property)
      });

      this.refresh();
   }

   refresh(property?:string) {
      this.loader.showSpinner();

      return this.reload().then(() => {
         if (property !== 'page') {
            this.model.checkedIds = [];
         }
      }).finally(() => {
         this.loader.hide();
      });
   }

   private reload() {
      return this.eventsApi.list(this.filter.inputs).then(data => {
         this.model.setList(data.list, data.total);
      });
   }

   select($event:MouseEvent, event) {
      // ignore clicks on duplicate/edit/copy actions
      if (!ng.element($event.target).parents('td').hasClass('actions')) {
         // setting selected now makes the UI appear to respond immediately
         // while the getEvent function fetches the event details
         this.model.selected = event;
         this.activeTab = 0;
         this.getEvent(event.id);
      }
   }

   remove(event) {
      this.dialogs.confirm(`Are you sure you want to delete "${event.name}"`).then(() => {
         this.loader.showSpinner();

         return this.eventsApi.del(event.id)
            .then(() => this.onDeleteSuccess())
            .catch(resp => this.onDeleteFailed(resp, event.id));
      }).finally(() => {
         this.loader.hide();
         this.model.checkedIds = [];
      });
   }

   removeBulk() {
      let ids = this.model.checkedIds;

      this.dialogs.confirm(`Are you sure you want to delete ${ids.length} events?`).then(() => {
         this.loader.showSpinner();

         return this.eventsApi.del(ids)
            .then(() => this.onDeleteSuccess())
            .catch(data => this.onDeleteFailed(data, ids));
      }).finally(() => {
         this.loader.hide();
         this.model.checkedIds = [];
      });
   }

   exportCsv() {
      this.loader.showMessage(
         'We are preparing your export',
         'This may take several moments if there are a lot of records.'
      );

      this.eventsApi.download(this.filter.inputs)
         .finally(() => {
            this.loader.hide();
         });
   }

   private getEvent(eventId:number) {
      this.eventsApi.get(eventId).then(data => {
         this.model.selected = data;
      });
   }

   private onDeleteSuccess() {
      return this.reload().then(() => {
         this.toast.success()
            .message('Event(s) deleted!')
            .send();
      });
   }

   private onDeleteFailed(resp, eventId:number|number[]) {
      if (resp.status != 409) {
         return resp;
      }

      let associations = [];

      if (resp.data.participantCount > 0) {
         associations.push(resp.data.participantCount + ' participants');
      }

      if (resp.data.preRegistrationCount > 0) {
         associations.push(resp.data.preRegistrationCount + ' pre-registrations');
      }

      if (resp.data.inviteeCount > 0) {
         associations.push(resp.data.inviteeCount + ' invitees');
      }

      let message:string;
      if (ng.isArray(eventId)) {
         message = `These events have ${associations.join(', ')} associated with them and could not be deleted.`;
      } else {
         message = `This event has ${associations.join(', ')} associated with it and could not be deleted.`;
      }

      if (this.session.isSuperAdmin()) {
         if (ng.isArray(eventId)) {
            message += " Press OK if you're sure you would like to delete these events";
         } else {
            message += " Press OK if you're sure you would like to delete this event";
         }
         message += ' and all associated participants, pre-registrations and invitees.';

         return this.dialogs.confirm(message).then(() => {
            return this.eventsApi.del(eventId, true)
               .then(() => this.onDeleteSuccess());
         });
      } else {
         return this.dialogs.alert(message);
      }
   }
}

ng.module('e360')
   .controller('EventListController', EventListController);
